import React, { useState } from 'react';

import {
  Td,
  Stack,
  HStack,
  Heading,
  Icon,
  Button,
  Tr,
  Text,
  useColorModeValue,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
  Link,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiCalendar } from 'react-icons/bi';
import { FiPieChart, FiHelpCircle } from 'react-icons/fi';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { IoChevronForwardOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { Link as RouterLink, useLocation } from 'react-router';

import { CompareDataset, useCanCompare, useCompare, useRouteDatasetRef } from 'components/shared/Compare';
import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import { AccessResourceKey } from 'hooks/data/files/types';
import { Product } from 'hooks/data/search/types';
import useSearchFilters from 'hooks/data/search/useSearchFilters';
import useAccess from 'hooks/useAccess';
import { useIsShowingV4 } from 'hooks/useV4';
import useVersionInfo from 'hooks/useVersionInfo';
import { newerVersions } from 'pages/Search/utils';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';
import { saveSearchOrigin } from 'utilities/search/searchOrigin';

import { activityTypeCopy, activityTypes } from './constants';

type Props = {
  isLastGroup?: boolean;
  activity: Product['activities'][0];
  index: number;
  currentlyDisplayed: number;
  resultPosition: number;
  productName: string;
  unit: string;
  groupDatasetIds: number[];
};

const ActivityRow = ({
  isLastGroup,
  activity,
  index,
  currentlyDisplayed,
  resultPosition,
  productName,
  unit,
  groupDatasetIds,
}: Props) => {
  const isLastCurrentlyVisibleGeography = index === activity.datasets.length - 1 || index === currentlyDisplayed - 1;
  const doMoreDatasetsExist = activity.datasets.length > currentlyDisplayed;

  const shouldHideLastDatasetBorder = isLastCurrentlyVisibleGeography && (isLastGroup || doMoreDatasetsExist);

  const shouldHideActivityColumnBorder = isLastGroup || doMoreDatasetsExist;

  const [hovering, setHovering] = useState(false);
  const { ver, system_model } = useVersionInfo();
  const { search } = useLocation();
  const { data: accessData } = useAccess();
  const {
    filterValues: { query, ...otherFilters },
  } = useSearchFilters();
  const { isOpen: timePeriodIsOpen, onClose: onTimePeriodClose, onToggle: onTimePeriodToggle } = useDisclosure();
  const { isOpen: activityIsOpen, onClose: onActivityClose, onToggle: onActivityToggle } = useDisclosure();

  const hasScoresAccess = checkAccessToResource(accessData, AccessResourceKey.SCORES, ver);

  const grayText = useColorModeValue('gray.700', 'gray.400');
  const iconColor = useColorModeValue('green.500', 'green.300');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const compareColor = useColorModeValue('gray.800', 'gray.100');
  const selectedBg = useColorModeValue('green.50', 'green.900');

  const shouldIncludeActivityName = index === 0;
  const dataset = activity.datasets[index];

  const { toggleDataset, isSelected, isFull, datasets: comparedDatasets } = useCompare();
  const canCompare = useCanCompare();
  const routeRef = useRouteDatasetRef();
  const datasetKey = String(dataset.id);
  const selected = canCompare(ver) && isSelected(routeRef(dataset.id));
  const groupSelected = canCompare(ver) && groupDatasetIds.some((id) => isSelected(routeRef(id)));

  const compareItem: CompareDataset = {
    id: datasetKey,
    version: ver,
    systemModel: system_model,
    activityName: activity.name,
    referenceProduct: productName,
    geography: dataset.geography,
    unit,
    activityType: activity.activity_type,
    impactScore: typeof dataset.impact_score === 'number' ? dataset.impact_score : undefined,
    impactUnit: dataset.impact_unit,
    sector: activity.sectors.join(', '),
    timePeriod: activity.time_period,
  };

  const isShowingV4 = useIsShowingV4();

  const onCloseActivityPopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Activity Type' });
    onActivityClose();
  };

  const onCloseTimePeriodPopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Time Period' });
    onTimePeriodClose();
  };

  const onToggleCompare = () => {
    mixpanel.track(selected ? MIXPANEL_EVENTS.COMPARE_DATASET_REMOVED : MIXPANEL_EVENTS.COMPARE_DATASET_ADDED, {
      dataset_id: datasetKey,
      dataset_version: ver,
      dataset_system_model: system_model,
      source: 'search_product_row',
      selection_count: selected ? comparedDatasets.length - 1 : comparedDatasets.length + 1,
      geography: dataset.geography,
      result_position: resultPosition,
      dataset_index: index,
    });
    toggleDataset(compareItem);
  };

  return (
    <Tr onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      {shouldIncludeActivityName && (
        <Td
          w={{ base: '100%', xl: '450px' }}
          verticalAlign={'top'}
          pl={0}
          py={0}
          rowSpan={Math.min(currentlyDisplayed, activity.datasets.length)}
          borderColor={borderColor}
          borderBottomWidth={shouldHideActivityColumnBorder ? 0 : 1}
          pb={shouldHideActivityColumnBorder ? 0 : 1}
          bg={groupSelected ? selectedBg : undefined}
        >
          <Stack key={activity.name} spacing={4} p={4} pb={shouldHideActivityColumnBorder ? 2 : 4}>
            <HStack justifyContent="space-between">
              <Heading size="sm">{activity.name}</Heading>
              <HStack spacing={0.5}>
                <Icon as={HiOutlineDocumentReport} color={grayText} />
                <Text color={grayText}>{activity.datasets.length}</Text>
              </HStack>
            </HStack>
            <Stack fontSize={'sm'}>
              <HStack>
                <Icon as={FiPieChart} color={grayText} />
                <Text color={grayText}>{activityTypes[activity.activity_type]}</Text>
                <Popover isOpen={activityIsOpen} onClose={onCloseActivityPopover} strategy="fixed">
                  <PopoverTrigger>
                    <Stack cursor={'pointer'}>
                      <Icon
                        as={FiHelpCircle}
                        color={iconColor}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Activity Type' });
                          onActivityToggle();
                        }}
                      />
                    </Stack>
                  </PopoverTrigger>
                  <PopoverContent px={6} py={5}>
                    <PopoverArrow />
                    <PopoverHeader borderBottom="none" p={0}>
                      <Heading size="sm">{activityTypes[activity.activity_type]}</Heading>
                    </PopoverHeader>
                    <PopoverBody p={0} mt={4}>
                      {activityTypeCopy[activity.activity_type]?.text}
                    </PopoverBody>
                    <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                      <Button width={{ base: '100%', md: 'auto' }} onClick={onActivityClose} variant="ghost">
                        Close
                      </Button>
                      {activityTypeCopy[activity.activity_type]?.link && (
                        <Button
                          width={{ base: '100%', md: 'auto' }}
                          onClick={() => {
                            mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Activity Type' });
                            onActivityClose();
                          }}
                          as={Link}
                          isExternal
                          sx={{ '&:hover': { textDecoration: 'none' } }}
                          href={activityTypeCopy[activity.activity_type]?.link}
                        >
                          Learn More
                        </Button>
                      )}
                    </Stack>
                  </PopoverContent>
                </Popover>
              </HStack>

              {!isShowingV4 && (
                <HStack>
                  <Icon as={BiCalendar} color={grayText} />

                  <Text color={grayText}>{activity.time_period}</Text>
                  <Popover isOpen={timePeriodIsOpen} onClose={onCloseTimePeriodPopover} strategy="fixed">
                    <PopoverTrigger>
                      <Stack cursor={'pointer'}>
                        <Icon
                          as={FiHelpCircle}
                          color={iconColor}
                          onClick={() => {
                            mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Time Period' });
                            onTimePeriodToggle();
                          }}
                        />
                      </Stack>
                    </PopoverTrigger>
                    <PopoverContent px={6} py={5}>
                      <PopoverArrow />
                      <PopoverHeader p={0} borderBottom="none">
                        <Heading size="sm">Time Period</Heading>
                      </PopoverHeader>
                      <PopoverBody p={0} mt={4}>
                        <Stack spacing={3}>
                          <Text>
                            The time period reflects the original data collection or extrapolation period, not an expiry date. A
                            dataset may have a past end date but remain valid if technologies and supply chains have not
                            significantly changed.
                          </Text>
                        </Stack>
                      </PopoverBody>
                      <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                        <Button width={{ base: '100%', md: 'auto' }} onClick={onTimePeriodClose} variant="ghost">
                          Close
                        </Button>
                        <Button
                          width={{ base: '100%', md: 'auto' }}
                          onClick={() => {
                            mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Time Period' });
                            onTimePeriodClose();
                          }}
                          as={Link}
                          isExternal
                          sx={{ '&:hover': { textDecoration: 'none' } }}
                          href={
                            'https://support.ecoinvent.org/activities-products#Activities:~:text=of%20Europe%20(RER).-,Its%20Time%20Period%C2%A0,-The%20time%20period'
                          }
                        >
                          Learn More
                        </Button>
                      </Stack>
                    </PopoverContent>
                  </Popover>
                </HStack>
              )}
            </Stack>
          </Stack>
        </Td>
      )}
      <Td
        borderColor={borderColor}
        py={3}
        borderBottomWidth={shouldHideLastDatasetBorder ? 0 : 1}
        bg={selected ? selectedBg : undefined}
      >
        <HStack spacing={2}>
          {selected && <Icon as={IoCheckmarkCircle} color={iconColor} />}
          <Text as="span">{dataset.geography}</Text>
        </HStack>
      </Td>

      {hasScoresAccess && newerVersions.includes(ver) && (
        <Td
          borderColor={borderColor}
          whiteSpace={'nowrap'}
          borderBottomWidth={shouldHideLastDatasetBorder ? 0 : 1}
          bg={selected ? selectedBg : undefined}
        >
          {typeof dataset.impact_score === 'number' ? (
            <HStack justifyContent="flex-end">
              <Text fontSize="md">{formatNumericalValue(dataset.impact_score)}</Text>
              <Text color={grayText}>{dataset.impact_unit}</Text>
              <CopyToClipboardButton
                value={dataset.impact_score}
                opacity={hovering ? 1 : 0}
                onValueCopied={() => {
                  mixpanel.track(MIXPANEL_EVENTS.COPY_SCORE_ON_SEARCH_RESULTS, {
                    score: dataset.impact_score,
                    name: activity.name,
                    id: dataset.id,
                    geography: dataset.geography,
                  });
                }}
              />
            </HStack>
          ) : (
            <HStack justifyContent="flex-end" pr={14}>
              <Text color={grayText}>N/A</Text>
            </HStack>
          )}
        </Td>
      )}
      <Td
        borderColor={borderColor}
        borderBottomWidth={shouldHideLastDatasetBorder ? 0 : 1}
        bg={selected ? selectedBg : undefined}
      >
        <HStack spacing={4} whiteSpace={'nowrap'}>
          {canCompare(ver) && (
            <Button variant="link" size="sm" color={compareColor} isDisabled={!selected && isFull} onClick={onToggleCompare}>
              Compare
            </Button>
          )}
          <Button
            variant="link"
            alignItems="center"
            as={RouterLink}
            size="sm"
            to={`/${ver}/${system_model}/dataset/${dataset.id}`}
            onClick={() => {
              saveSearchOrigin({ ver, system_model, search, spold_id: dataset.id });
              mixpanel.track(MIXPANEL_EVENTS.SEARCH_RESULT_CLICKED, {
                datasetId: dataset.id,
                query,
                filters: otherFilters,
                result_type: otherFilters.searchBy,
                result_position: resultPosition,
                dataset_index: index,
              });
            }}
          >
            <Text>View Dataset</Text> <Icon as={IoChevronForwardOutline} />
          </Button>
        </HStack>
      </Td>
    </Tr>
  );
};

export default ActivityRow;
